import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { SlotConflictBanner } from '../../components/SlotConflictBanner';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../hooks/useToast';
import {
  ApiError,
  getTournament,
  listTables,
  listTournamentParticipants,
  patchTournament,
  slotConflictDetail,
} from '../../lib/api';
import {
  EMPTY_TOURNAMENT_FORM,
  buildPatchTournamentBody,
  formFromTournament,
  tournamentFormErrors,
  type TournamentFormValue,
} from '../../lib/tournament-form';
import { hasEverConfirmedParticipant } from '../../lib/tournaments';
import { PS, T } from '../../lib/theme';
import type { AdminParticipant, AdminTournament, SlotConflictDetail, TableEmbed } from '../../lib/types';
import { TournamentFieldsForm } from './TournamentFieldsForm';

export function EditTournamentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const tournamentId = id ? Number(id) : NaN;
  const validId = Number.isSafeInteger(tournamentId) && tournamentId > 0;

  const detailLoader = useCallback(
    (signal: AbortSignal) => validId
      ? getTournament(tournamentId, signal)
      : Promise.reject(new Error('Invalid tournament id.')),
    [tournamentId, validId],
  );
  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);
  const participantsLoader = useCallback(
    (signal: AbortSignal) => validId
      ? listTournamentParticipants(tournamentId, signal)
      : Promise.reject(new Error('Invalid tournament id.')),
    [tournamentId, validId],
  );
  const detail = useApi<AdminTournament>(detailLoader);
  const tables = useApi<TableEmbed[]>(tablesLoader);
  const participants = useApi<AdminParticipant[]>(participantsLoader);

  const [tournament, setTournament] = useState<AdminTournament | null>(null);
  const [form, setForm] = useState<TournamentFormValue>(EMPTY_TOURNAMENT_FORM);
  const [originalForm, setOriginalForm] = useState<TournamentFormValue>(EMPTY_TOURNAMENT_FORM);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<SlotConflictDetail | null>(null);

  useEffect(() => {
    if (!detail.data) return;
    const nextForm = formFromTournament(detail.data);
    setTournament(detail.data);
    setForm(nextForm);
    setOriginalForm(nextForm);
  }, [detail.data]);

  if (!validId) {
    return <div style={{ ...T.ui, color: PS.danger }}>Invalid tournament id.</div>;
  }

  const patch = (change: Partial<TournamentFormValue>) => setForm((current) => ({ ...current, ...change }));
  const patchBody = buildPatchTournamentBody(originalForm, form);
  const hasChanges = Object.keys(patchBody).length > 0;
  const formErrors = tournamentFormErrors(form);
  const canSave = Boolean(tournament) && hasChanges && formErrors.length === 0 && !saving;
  const frozenHint = hasEverConfirmedParticipant(participants.data ?? []);
  const title = tournament ? tournament.translations.en?.title ?? tournament.title : 'Edit tournament';

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!tournament || !canSave) return;
    setSaving(true);
    setConflict(null);
    try {
      await patchTournament(tournament.id, patchBody);
      push('success', 'Tournament updated.');
      // Re-read the canonical persisted response rather than trusting this draft.
      navigate(`/tournaments/${tournament.id}`);
    } catch (err) {
      const detail = slotConflictDetail(err);
      if (detail) {
        setConflict(detail);
        push('error', `${detail.conflicts.length} conflict${detail.conflicts.length === 1 ? '' : 's'} — resolve below and retry.`);
      } else {
        push('error', err instanceof ApiError ? err.message : 'Update failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setForm(originalForm);
    setConflict(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 800 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>{title}</h1>
        <Button variant="secondary" onClick={() => navigate(`/tournaments/${id ?? ''}`)}>← Back to overview</Button>
      </header>

      {detail.loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading tournament…</div>}
      {detail.error && <ErrorPanel message={detail.error.message} />}

      {tournament && (
        <form onSubmit={(event) => void handleSave(event)} style={formStyle}>
          <TournamentFieldsForm
            value={form}
            onChange={patch}
            tables={tables.data ?? []}
            tablesLoading={tables.loading}
            frozenHint={frozenHint}
          />
          {tables.error && <ErrorPanel message={`Could not load tables: ${tables.error.message}`} />}
          {participants.error && <ErrorPanel message={`Could not load participants: ${participants.error.message}`} />}
          {formErrors.length > 0 && (
            <ul style={{ ...T.caption, color: PS.errorText, margin: 0, paddingLeft: 18 }}>
              {formErrors.map((error) => <li key={error}>{error}</li>)}
            </ul>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button type="submit" disabled={!canSave}>{saving ? 'Saving…' : 'Save changes'}</Button>
            <Button type="button" variant="secondary" onClick={handleDiscard} disabled={!hasChanges || saving}>Discard changes</Button>
            <Button type="button" variant="secondary" onClick={() => navigate(`/tournaments/${tournament.id}`)} disabled={saving}>Cancel</Button>
          </div>
          {conflict && <SlotConflictBanner detail={conflict} />}
        </form>
      )}
    </div>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div role="alert" style={{ padding: 12, background: PS.errorBg, border: `1px solid ${PS.errorBorder}`, color: PS.errorText, borderRadius: 3 }}>
      {message}
    </div>
  );
}

const formStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 20,
  background: PS.white,
  padding: 20,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 4,
};
